import {
  DashboardColumnType,
  IAnalysisSummaryInfoFieldsFragment,
  IDashboardAnalysisAddMutation,
  IDashboardAnalysisAddMutationVariables,
  IDashboardInstanceFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { ModalSliceType, openModal } from '../../../../redux/slices/modal';
import { useAppDispatch } from '../../../../redux/hooks';
import useDashboardUpdateGrid from '../grid/useDashboardUpdateGrid';
import { useMutation } from '@apollo/client';
import { DASHBOARD_ANALYSIS_ADD } from '../../../../utils/apollo/dashboards';
import { useCallback } from 'react';
import { MaybeNil } from '../../../../utils/interfaces';
import { IGridDropPosition } from '../../../../components/DndGrid';
import { assertNotNil } from '../../../../utils/types';
import { getRowsWithAddedContent } from '../grid/gridUpdaters';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import useDashboardReload from '../useDashboardReload';

const useDashboardAddReport = (dashboard: IDashboardInstanceFieldsFragment) => {
  const dispatch = useAppDispatch();
  const reloadDashboard = useDashboardReload(dashboard.baseId);
  const { saveDashboardGrid } = useDashboardUpdateGrid(dashboard);

  const [addReportMutation] = useMutation<
    IDashboardAnalysisAddMutation,
    IDashboardAnalysisAddMutationVariables
  >(DASHBOARD_ANALYSIS_ADD);

  const addReport = useCallback(
    async (position: MaybeNil<IGridDropPosition>) => {
      const handleAddReport = async (
        selectedReport: IAnalysisSummaryInfoFieldsFragment,
      ) => {
        const resp = await addReportMutation({
          variables: {
            input: {
              baseId: dashboard.baseId,
              analysis: {
                analysisId: selectedReport.id,
                name: selectedReport.display,
                pref: null,
                description: selectedReport.description,
              },
            },
          },
        });

        const result = resp.data?.dashboardAnalysisAdd;
        if (printGraphqlErrors(result?.errors)) {
          await reloadDashboard();
          return;
        }

        const dashboardAnalysis = result?.analysis;
        assertNotNil(dashboardAnalysis);

        await saveDashboardGrid(
          getRowsWithAddedContent({
            type: DashboardColumnType.Analysis,
            analysis: dashboardAnalysis,
            position,
          }),
        );
      };

      dispatch(
        openModal({
          type: ModalSliceType.ReportsSearch,
          data: {
            onSubmit: handleAddReport,
          },
          id: 'dashboard',
        }),
      );
    },
    [
      addReportMutation,
      dashboard.baseId,
      dispatch,
      reloadDashboard,
      saveDashboardGrid,
    ],
  );

  return {
    addReport,
  };
};

export default useDashboardAddReport;
